import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/**
 * Order administration (spec §22, §23, §24, §27, §33).
 *
 * Every write here goes through the order's state machine on the server, which
 * decides both whether a move is legal and what it costs in stock and deposit.
 * Nothing in this module reasons about that — it posts the intent and re-reads
 * the order the server returns.
 */

export type OrderStatus =
  | 'PENDING_PAYMENT'
  | 'CONFIRMED'
  | 'PROCESSING'
  | 'READY_TO_SHIP'
  | 'SHIPPED'
  | 'IN_TRANSIT'
  | 'DELIVERED'
  | 'CANCELLED'
  | 'REJECTED'
  | 'RETURN_REQUESTED'
  | 'RETURNED'
  | 'COMPLETED';

export type PaymentStatus =
  | 'CREATED'
  | 'PENDING'
  | 'AUTHORIZED'
  | 'PAID'
  | 'FAILED'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED';

export type FulfillmentStatus = 'UNFULFILLED' | 'PARTIALLY_FULFILLED' | 'FULFILLED';

export type ShipmentStatus =
  | 'PENDING'
  | 'PICKED_UP'
  | 'IN_TRANSIT'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'FAILED'
  | 'RETURNED';

export type InvoiceStatus = 'DRAFT' | 'ISSUED' | 'PAID' | 'CANCELLED';

export interface AddressSnapshot {
  label: string;
  contactName: string;
  contactPhone: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  country: string;
  pincode: string;
  gstin: string | null;
}

export interface OrderItem {
  id: string;
  productId: string | null;
  productName: string;
  productSku: string;
  variantName: string | null;
  hsnCode: string | null;
  quantity: number;
  unitPrice: number;
  listPrice: number;
  discountAmount: number;
  taxPercent: number;
  taxAmount: number;
  lineTotal: number;
  moqAtPurchase: number | null;
}

export interface OrderStatusEntry {
  id: string;
  fromStatus: OrderStatus | null;
  toStatus: OrderStatus;
  notes: string | null;
  changedBy: string | null;
  createdAt: string;
}

export interface Shipment {
  id: string;
  shipmentNumber: string;
  carrier: string;
  trackingNumber: string | null;
  trackingUrl: string | null;
  shippingMethod: string | null;
  status: ShipmentStatus;
  estimatedDeliveryDate: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  notes: string | null;
}

export interface OrderCustomer {
  id: string;
  customerCode: string;
  companyName: string;
}

export interface OrderRow {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  fulfillmentStatus: FulfillmentStatus;
  totalAmount: number;
  totalQuantity: number;
  itemCount: number;
  currency: string;
  placedAt: string;
  customer: OrderCustomer | null;
  assignedTo: { id: string; name: string } | null;
}

export interface OrderDetail extends OrderRow {
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  shippingAmount: number;
  securityDepositAdjustment: number;
  notes: string | null;
  /** Staff-only. Never returned to the buyer. */
  internalNotes: string | null;
  billingAddress: AddressSnapshot | null;
  shippingAddress: AddressSnapshot | null;
  items: OrderItem[];
  statusHistory: OrderStatusEntry[];
  shipments: Shipment[];
  invoice: { id: string; invoiceNumber: string; status: InvoiceStatus } | null;
  warehouse: { id: string; name: string; code: string } | null;
  customer: (OrderCustomer & { email: string | null; mobile: string | null }) | null;
  confirmedAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
}

export interface Payment {
  id: string;
  paymentNumber: string;
  orderId: string | null;
  purpose: string;
  provider: string;
  status: PaymentStatus;
  amount: number;
  refundedAmount: number;
  currency: string;
  method: string | null;
  reference: string | null;
  failureReason: string | null;
  paidAt: string | null;
  createdAt: string;
}

/** What is paid and what is left on one order. */
export interface OrderPaymentState {
  orderId: string;
  orderNumber: string;
  totalAmount: number;
  paidAmount: number;
  outstandingAmount: number;
  paymentStatus: PaymentStatus;
  payments: Payment[];
}

export interface OrderSummary {
  pending: number;
  confirmed: number;
  processing: number;
  shipped: number;
  delivered: number;
  cancelled: number;
  totalRevenue: number;
}

export interface OrderListParams {
  page?: number;
  limit?: number;
  status?: OrderStatus[];
  paymentStatus?: PaymentStatus;
  customerId?: string;
  search?: string;
}

const keys = {
  all: ['admin', 'orders'] as const,
  list: (params: OrderListParams) => [...keys.all, 'list', params] as const,
  summary: () => [...keys.all, 'summary'] as const,
  detail: (orderId: string) => [...keys.all, 'detail', orderId] as const,
  payments: (orderId: string) => [...keys.all, 'payments', orderId] as const,
};

export function useOrders(params: OrderListParams) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<OrderRow>>('/admin/orders', {
        query: {
          page: params.page,
          limit: params.limit,
          status: params.status?.length ? params.status.join(',') : undefined,
          paymentStatus: params.paymentStatus,
          customerId: params.customerId,
          search: params.search || undefined,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useOrderSummary() {
  return useQuery({
    queryKey: keys.summary(),
    queryFn: () => apiClient.get<OrderSummary>('/admin/orders/summary'),
  });
}

export function useOrder(orderId: string | null) {
  return useQuery({
    queryKey: keys.detail(orderId ?? ''),
    queryFn: () => apiClient.get<OrderDetail>(`/admin/orders/${orderId}`),
    enabled: Boolean(orderId),
  });
}

export function useOrderPayments(orderId: string | null) {
  return useQuery({
    queryKey: keys.payments(orderId ?? ''),
    queryFn: () => apiClient.get<OrderPaymentState>(`/admin/orders/${orderId}/payments`),
    enabled: Boolean(orderId),
  });
}

/**
 * Any order write invalidates the list, the counters and the detail.
 *
 * Deposits and inventory go too: confirming an order holds deposit and commits
 * stock, and cancelling releases both. Those screens would otherwise show
 * figures this action has already changed.
 */
function useOrderMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'deposits'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'inventory'] });
    },
  });
}

export function useChangeOrderStatus() {
  return useOrderMutation(
    ({
      orderId,
      ...body
    }: {
      orderId: string;
      status: OrderStatus;
      notes?: string;
    }) => apiClient.patch(`/admin/orders/${orderId}/status`, body),
  );
}

export function useCancelOrder() {
  return useOrderMutation(({ orderId, reason }: { orderId: string; reason: string }) =>
    apiClient.post(`/admin/orders/${orderId}/cancel`, { reason }),
  );
}

export function useAssignOrder() {
  return useOrderMutation(
    ({
      orderId,
      ...body
    }: {
      orderId: string;
      assignedToId?: string;
      warehouseId?: string;
      internalNotes?: string;
    }) => apiClient.patch(`/admin/orders/${orderId}/assignment`, body),
  );
}

export function useRecordOrderPayment() {
  return useOrderMutation(
    ({
      orderId,
      ...body
    }: {
      orderId: string;
      amount: number;
      reference?: string;
      method?: string;
      notes?: string;
    }) => apiClient.post(`/admin/orders/${orderId}/payments`, body),
  );
}

export function useGenerateInvoice() {
  return useOrderMutation(({ orderId }: { orderId: string }) =>
    apiClient.post(`/admin/orders/${orderId}/invoice`),
  );
}

export function useCreateShipment() {
  return useOrderMutation(
    ({
      orderId,
      ...body
    }: {
      orderId: string;
      carrier: string;
      trackingNumber?: string;
      trackingUrl?: string;
      shippingMethod?: string;
      estimatedDeliveryDate?: string;
      notes?: string;
    }) => apiClient.post(`/admin/orders/${orderId}/shipments`, body),
  );
}

// --- Presentation helpers ----------------------------------------------------

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING_PAYMENT: 'Awaiting payment',
  CONFIRMED: 'Confirmed',
  PROCESSING: 'Processing',
  READY_TO_SHIP: 'Ready to ship',
  SHIPPED: 'Shipped',
  IN_TRANSIT: 'In transit',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
  RETURN_REQUESTED: 'Return requested',
  RETURNED: 'Returned',
  COMPLETED: 'Completed',
};

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  CREATED: 'Not paid',
  PENDING: 'Pending',
  AUTHORIZED: 'Authorised',
  PAID: 'Paid',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
  PARTIALLY_REFUNDED: 'Partially refunded',
};

export const SHIPMENT_STATUS_LABEL: Record<ShipmentStatus, string> = {
  PENDING: 'Preparing',
  PICKED_UP: 'Picked up',
  IN_TRANSIT: 'In transit',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  FAILED: 'Delivery failed',
  RETURNED: 'Returned',
};

/**
 * Which statuses each status may move to.
 *
 * A copy of `ORDER_TRANSITIONS` on the server, kept here so the drawer offers
 * only legal moves instead of a dropdown of everything followed by a rejection.
 * The server re-checks every transition, so a stale copy costs a failed write,
 * never an illegal one.
 */
export const ORDER_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  PENDING_PAYMENT: ['CONFIRMED', 'CANCELLED', 'REJECTED'],
  CONFIRMED: ['PROCESSING', 'CANCELLED', 'REJECTED'],
  PROCESSING: ['READY_TO_SHIP', 'CANCELLED'],
  READY_TO_SHIP: ['SHIPPED', 'CANCELLED'],
  // Once goods have left the warehouse the order cannot be cancelled; it comes
  // back as a return, which is a different process with different money.
  SHIPPED: ['IN_TRANSIT', 'DELIVERED'],
  IN_TRANSIT: ['DELIVERED'],
  DELIVERED: ['COMPLETED', 'RETURN_REQUESTED'],
  RETURN_REQUESTED: ['RETURNED', 'DELIVERED'],
  RETURNED: ['COMPLETED'],
  COMPLETED: [],
  CANCELLED: [],
  REJECTED: [],
};

// --- Payments across every order --------------------------------------------

export interface PaymentListParams {
  page?: number;
  limit?: number;
  search?: string;
  customerId?: string;
}

const paymentKeys = {
  all: ['admin', 'payments'] as const,
  list: (params: PaymentListParams) => [...paymentKeys.all, 'list', params] as const,
};

export function usePayments(params: PaymentListParams) {
  return useQuery({
    queryKey: paymentKeys.list(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<Payment>>('/admin/payments', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          customerId: params.customerId,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export const PAYMENT_PURPOSE_LABEL: Record<string, string> = {
  SECURITY_DEPOSIT: 'Security deposit',
  ORDER: 'Order payment',
  PARTIAL_ORDER: 'Part payment',
  ADDITIONAL_CHARGE: 'Additional charge',
};

export const PAYMENT_PROVIDER_LABEL: Record<string, string> = {
  RAZORPAY: 'Razorpay',
  STRIPE: 'Stripe',
  BANK_TRANSFER: 'Bank transfer',
  CASH_ON_DELIVERY: 'Cash on delivery',
  MANUAL: 'Recorded manually',
};
